import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { normalizeEventNameList } from '../config/eventTypes';
import {
  AirplaneTilt,
  ArrowDown,
  ArrowUpRight,
  Buildings,
  CalendarBlank,
  CaretLeft,
  CaretRight,
  Check,
  Clock,
  EnvelopeSimple,
  FacebookLogo,
  Globe,
  Heart,
  ImageSquare,
  InstagramLogo,
  LinkedinLogo,
  MapPin,
  Play,
  Quotes,
  Star,
  SuitcaseRolling,
  Users,
  WhatsappLogo,
  X,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import GalagoLogo from '../components/GalagoLogo';
import EventAwareDatePicker from '../components/EventAwareDatePicker';
import api from '../services/api';
import { resolveMediaUrl } from '../utils/media';
import { getEventTypeConfig, isEventFeatureEnabled } from '../config/eventTypes';

const SOCIAL_LINKS = [
  ['instagram', InstagramLogo, 'Instagram'],
  ['facebook', FacebookLogo, 'Facebook'],
  ['linkedin', LinkedinLogo, 'LinkedIn'],
  ['website', Globe, 'Website'],
];

const VENDOR_LABELS = {
  photographer: 'Photography',
  videographer: 'Films',
  decorator: 'Decor',
  dj: 'Entertainment',
  caterer: 'Catering',
  venue: 'Venue',
  makeup: 'Makeup',
  transportation: 'Transportation',
  florist: 'Florals',
  other: 'Creative Partners',
};

function formatDateRange(event) {
  if (!event?.start_date) return '';
  const start = new Date(event.start_date);
  if (!event.end_date) return format(start, 'MMMM yyyy');
  const end = new Date(event.end_date);
  return `${format(start, 'd MMM')} - ${format(end, 'd MMM yyyy')}`;
}

function formatEventMonth(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : format(date, 'MMMM yyyy');
}

function destinationLabel(event) {
  return event?.city || event?.venues?.[0]?.name || 'Destination celebration';
}

function eventHostLabel(event) {
  const names = event?.event_type === 'wedding'
    ? [event?.bride_name, event?.groom_name].filter(Boolean)
    : event?.event_type === 'birthday'
      ? normalizeEventNameList(event?.honoree_names)
    : (event?.host_names || []).filter(Boolean);
  return names.length ? names.join(' & ') : event?.name;
}

function eventTypeLabel(event) {
  if (event?.event_type === 'baby_shower') return 'Baby Shower Celebration';
  if (event?.event_type === 'birthday') return 'Birthday Celebration';
  if (event?.event_type === 'corporate') return 'Corporate Event';
  return 'Wedding Celebration';
}

function normalizePhone(value = '') {
  return value.replace(/[^0-9]/g, '');
}

function inferGalleryCategory(item, moments, index, galleryFilters) {
  if (
    item.category
    && item.category.toLowerCase() !== 'all'
    && galleryFilters.some((category) => category.toLowerCase() === item.category.toLowerCase())
  ) return item.category;
  const searchable = `${item.url || ''} ${(item.tags || []).join(' ')}`.toLowerCase();
  const matched = galleryFilters.slice(1).find((category) => (
    searchable.includes(category.toLowerCase())
  ));
  if (matched) return matched;
  return moments.length ? moments[index % moments.length].name : 'Celebration';
}

function SectionHeading({ eyebrow, title, copy, align = 'left', light = false }) {
  const centered = align === 'center';
  return (
    <div className={centered ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl'}>
      {eyebrow && (
        <p className={`mb-4 text-xs font-semibold uppercase tracking-[0.24em] ${light ? 'text-white/60' : 'text-[#9b7c48]'}`}>
          {eyebrow}
        </p>
      )}
      <h2 className={`font-serif text-4xl font-normal leading-tight md:text-5xl ${light ? 'text-white' : 'text-[#17211e]'}`}>
        {title}
      </h2>
      {copy && (
        <p className={`mt-5 text-base leading-8 md:text-lg ${light ? 'text-white/68' : 'text-[#5f6965]'}`}>
          {copy}
        </p>
      )}
    </div>
  );
}

function SummaryMetric({ icon: Icon, value, label }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex min-h-28 items-center gap-4 border-b border-[#e8e4dc] py-5 sm:border-b-0 sm:border-r sm:px-5 sm:first:pl-0 sm:last:border-r-0">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#eef1ed] text-[var(--showcase-primary)]">
        <Icon className="h-5 w-5" weight="light" />
      </span>
      <div>
        <p className="font-serif text-2xl text-[#17211e]">{value}</p>
        <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[#727b77]">{label}</p>
      </div>
    </div>
  );
}

function RatingStars({ rating = 5, className = '' }) {
  return (
    <div className={`flex gap-1 ${className}`} aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className="h-4 w-4 text-[var(--showcase-accent)]"
          weight={star <= Math.round(rating) ? 'fill' : 'regular'}
        />
      ))}
    </div>
  );
}

function MediaImage({ src, alt, className = '', loading = 'lazy' }) {
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-[#e9ece8] text-[#79827e] ${className}`}>
        <ImageSquare className="h-8 w-8" weight="light" />
      </div>
    );
  }
  return (
    <img
      src={resolveMediaUrl(src, { preferProxy: true })}
      alt={alt}
      className={className}
      loading={loading}
    />
  );
}

function EventCard({ event, slug, active }) {
  return (
    <Link
      to={`/showcase/${slug}/${event.id}`}
      className="group block min-w-0"
      aria-current={active ? 'page' : undefined}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#e9ece8]">
        <MediaImage
          src={event.cover_photo_url}
          alt={event.name || 'Featured event'}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/66 via-black/5 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
          <p className="text-xs uppercase tracking-[0.18em] text-white/72">
            {[event.city, formatDateRange(event)].filter(Boolean).join(' / ')}
          </p>
          <h3 className="mt-2 font-serif text-2xl">{event.name}</h3>
        </div>
        <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#17211e] opacity-0 transition-opacity group-hover:opacity-100">
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
      <div className="flex items-center justify-between border-b border-[#dcd8d0] py-4 text-sm text-[#5f6965]">
        <span>{event.theme_label || event.event_tags?.[0] || (event.event_type === 'corporate' ? 'Corporate Gala' : 'Signature Celebration')}</span>
        <span>{event.stats?.guests ? `${event.stats.guests} guests` : `${event.stats?.days || 1} days`}</span>
      </div>
    </Link>
  );
}

export default function PublicShowcasePage() {
  const { slug, eventId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [activeHeroIndex, setActiveHeroIndex] = useState(0);
  const [activeGalleryFilter, setActiveGalleryFilter] = useState('All');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [contact, setContact] = useState({
    name: '',
    phone: '',
    email: '',
    event_date: '',
    budget: '',
    location: '',
    requirements: '',
  });

  const fetchShowcase = useCallback(async (passwordValue = '') => {
    try {
      setLoading(true);
      const response = await api.get(`/public/showcase/${slug}`, {
        params: passwordValue ? { password: passwordValue } : {},
      });
      setData(response.data);
      setNeedsPassword(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setNeedsPassword(true);
      } else {
        toast.error(error.response?.data?.detail || 'Showcase not found');
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchShowcase();
  }, [fetchShowcase]);

  const selectedEvent = useMemo(() => {
    const events = data?.events || [];
    return events.find((event) => event.id === eventId) || events[0];
  }, [data?.events, eventId]);
  const eventTypeConfig = getEventTypeConfig(selectedEvent?.event_type);
  const showcaseConfig = eventTypeConfig.showcase;

  const profile = data?.profile || {};
  const branding = profile.branding || {};
  const stats = selectedEvent?.stats || {};
  const moments = useMemo(() => selectedEvent?.top_moments || [], [selectedEvent?.top_moments]);

  const heroMedia = useMemo(() => {
    const images = selectedEvent?.hero_images?.length
      ? selectedEvent.hero_images
      : [selectedEvent?.cover_photo_url, ...(selectedEvent?.gallery || [])];
    const uniqueImages = Array.from(new Set(images.filter(Boolean))).slice(0, 10);
    return [
      ...(selectedEvent?.cover_video_url ? [{ type: 'video', url: selectedEvent.cover_video_url }] : []),
      ...uniqueImages.map((url) => ({ type: 'image', url })),
    ];
  }, [selectedEvent]);

  const galleryItems = useMemo(() => {
    const media = selectedEvent?.gallery_media?.length
      ? selectedEvent.gallery_media
      : (selectedEvent?.gallery || []).map((url) => ({ url, category: 'All', tags: [] }));
    return media.map((item, index) => ({
      ...item,
      category: inferGalleryCategory(item, moments, index, showcaseConfig.galleryCategories),
    }));
  }, [selectedEvent, moments, showcaseConfig.galleryCategories]);

  const galleryFilters = useMemo(() => {
    const momentFilters = moments.map((moment) => moment.name).filter(Boolean);
    const storedFilters = galleryItems.map((item) => item.category).filter(Boolean);
    return Array.from(new Set([...showcaseConfig.galleryCategories, ...momentFilters, ...storedFilters]));
  }, [galleryItems, moments, showcaseConfig.galleryCategories]);

  const filteredGallery = useMemo(() => {
    if (activeGalleryFilter === 'All') return galleryItems;
    const needle = activeGalleryFilter.toLowerCase();
    return galleryItems.filter((item) => (
      item.category?.toLowerCase() === needle
      || (item.tags || []).some((tag) => String(tag).toLowerCase() === needle)
    ));
  }, [activeGalleryFilter, galleryItems]);

  const testimonials = useMemo(() => {
    const all = profile.testimonials || [];
    const eventSpecific = all.filter((item) => !item.event_id || item.event_id === selectedEvent?.id);
    return eventSpecific.length ? eventSpecific : (selectedEvent?.testimonials || []);
  }, [profile.testimonials, selectedEvent]);

  const vendorGroups = useMemo(() => {
    return (selectedEvent?.vendors || []).reduce((groups, vendor) => {
      const category = vendor.category || 'other';
      return { ...groups, [category]: [...(groups[category] || []), vendor] };
    }, {});
  }, [selectedEvent?.vendors]);

  useEffect(() => {
    setActiveHeroIndex(0);
    setActiveGalleryFilter('All');
    setTestimonialIndex(0);
  }, [selectedEvent?.id]);

  useEffect(() => {
    if (heroMedia.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveHeroIndex((current) => (current + 1) % heroMedia.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [heroMedia.length]);

  useEffect(() => {
    if (!selectedEvent) return;
    document.title = profile.seo?.title || `${selectedEvent.name} | ${profile.company_name || 'GalaGo Showcase'}`;
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement('meta');
      description.setAttribute('name', 'description');
      document.head.appendChild(description);
    }
    description.setAttribute('content', profile.seo?.description || selectedEvent.summary || '');
  }, [profile.company_name, profile.seo, selectedEvent]);

  useEffect(() => {
    if (!selectedEvent?.id) return;
    api.post(`/public/showcase/${slug}/analytics`, {
      action: 'event_view',
      event_id: selectedEvent.id,
    }).catch(() => {});
  }, [selectedEvent?.id, slug]);

  const recordInteraction = (action) => {
    api.post(`/public/showcase/${slug}/analytics`, {
      action,
      event_id: selectedEvent?.id,
    }).catch(() => {});
  };

  const openInquiry = (intent = '') => {
    if (intent) {
      setContact((current) => ({
        ...current,
        requirements: current.requirements || `${intent}: `,
      }));
    }
    setContactOpen(true);
  };

  const submitInquiry = async (event) => {
    event.preventDefault();
    try {
      setSending(true);
      await api.post(`/public/showcase/${slug}/inquiries`, {
        ...contact,
        source_event_id: selectedEvent?.id,
      });
      setContact({
        name: '',
        phone: '',
        email: '',
        event_date: '',
        budget: '',
        location: '',
        requirements: '',
      });
      setContactOpen(false);
      toast.success('Thank you. The planning team will be in touch shortly.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send inquiry');
    } finally {
      setSending(false);
    }
  };

  const openMoment = (moment) => {
    const available = galleryItems.some((item) => item.category?.toLowerCase() === moment.name?.toLowerCase());
    setActiveGalleryFilter(available ? moment.name : 'All');
    document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' });
    recordInteraction('gallery_interaction');
  };

  const whatsappNumber = normalizePhone(profile.contact?.phone || profile.contact_phone || '');
  const whatsappUrl = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hi, I found ${profile.company_name || 'your team'} through the GalaGo showcase and would like to discuss an event.`)}`
    : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f2ed]">
        <div className="h-[72px] animate-pulse bg-white" />
        <div className="h-[78vh] animate-pulse bg-[#dfe2de]" />
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f2f0eb] px-4">
        <form
          className="w-full max-w-md border border-[#ddd8cf] bg-white p-8 shadow-xl"
          onSubmit={(event) => {
            event.preventDefault();
            fetchShowcase(password);
          }}
        >
          <div className="mb-8 flex items-center gap-3">
            <GalagoLogo size={42} />
            <div>
              <h1 className="font-serif text-2xl text-[#17211e]">Private Portfolio</h1>
              <p className="mt-1 text-sm text-[#69726e]">Enter the shared password to continue.</p>
            </div>
          </div>
          <Label htmlFor="showcase-password">Password</Label>
          <Input
            id="showcase-password"
            type="password"
            className="mt-2 h-12 rounded-none"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoFocus
          />
          <Button className="mt-5 h-12 w-full rounded-none bg-[#173f35] hover:bg-[#173f35]/90" type="submit">
            Enter Portfolio
          </Button>
        </form>
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div className="min-h-screen bg-[#f4f2ed] text-[#17211e]">
        <header className="border-b border-black/10 bg-white">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-5 py-5">
            {profile.company_logo ? (
              <MediaImage src={profile.company_logo} alt={profile.company_name || ''} className="h-10 w-10 object-contain" loading="eager" />
            ) : <GalagoLogo size={40} />}
            <span className="font-serif text-xl">{profile.company_name || 'GalaGo Showcase'}</span>
          </div>
        </header>
        <main className="mx-auto max-w-4xl px-5 py-32 text-center">
          <h1 className="font-serif text-5xl">The next story is being prepared.</h1>
          <p className="mt-5 text-[#69726e]">Published celebrations will appear here soon.</p>
        </main>
      </div>
    );
  }

  const currentTestimonial = testimonials[testimonialIndex];
  const primaryColor = branding.primary_color || '#173F35';
  const accentColor = branding.accent_color || '#C9A96E';
  const executionMetrics = [
    [Users, stats.guests, 'Guests hosted'],
    ...(eventTypeConfig.features.showTravelAndId
      ? [[AirplaneTilt, stats.airport_pickups ?? stats.transport_trips, 'Airport pickups']]
      : []),
    [SuitcaseRolling, stats.vendors, 'Vendors coordinated'],
    [Users, stats.team_members, 'Team members'],
    ...(eventTypeConfig.features.showAccommodation
      ? [[Buildings, stats.rooms, 'Rooms managed'], [Buildings, stats.hotels, 'Hotels managed']]
      : []),
    [CalendarBlank, stats.functions, 'Functions delivered'],
    [Clock, stats.on_time_percentage ? `${stats.on_time_percentage}%` : undefined, 'On-time execution'],
  ].filter(([, value]) => value !== undefined && value !== null);

  return (
    <div
      className="min-h-screen overflow-x-hidden bg-[#f5f3ee] font-sans text-[#17211e]"
      style={{ '--showcase-primary': primaryColor, '--showcase-accent': accentColor, fontFamily: 'Inter, sans-serif' }}
    >
      <header className="sticky top-0 z-40 border-b border-black/10 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 lg:px-10">
          <Link to={`/showcase/${slug}`} className="flex min-w-0 items-center gap-3">
            {profile.company_logo ? (
              <MediaImage src={profile.company_logo} alt={profile.company_name || ''} className="h-10 w-10 shrink-0 object-contain" loading="eager" />
            ) : <GalagoLogo size={40} />}
            <div className="min-w-0">
              <p className="truncate font-serif text-lg leading-tight md:text-xl">{profile.company_name}</p>
              <p className="mt-0.5 text-[0.625rem] uppercase tracking-[0.2em] text-[#7b837f]">Events, thoughtfully delivered</p>
            </div>
          </Link>
          <nav className="hidden items-center gap-7 text-sm text-[#46504c] lg:flex">
            <a className="transition-colors hover:text-[var(--showcase-primary)]" href="#story">Story</a>
            <a className="transition-colors hover:text-[var(--showcase-primary)]" href="#moments">Moments</a>
            <a className="transition-colors hover:text-[var(--showcase-primary)]" href="#gallery">Gallery</a>
            <a className="transition-colors hover:text-[var(--showcase-primary)]" href="#about">About</a>
            <a className="transition-colors hover:text-[var(--showcase-primary)]" href="#events">Events</a>
          </nav>
          <Button
            onClick={() => openInquiry('Request a quote')}
            className="h-11 rounded-none px-5 text-white hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            Request a Quote
          </Button>
        </div>
      </header>

      <section className="relative h-[74svh] min-h-[520px] max-h-[840px] overflow-hidden bg-[#26332f] text-white md:h-[82svh] md:min-h-[620px]">
        {heroMedia.length ? heroMedia.map((media, index) => (
          media.type === 'video' ? (
            <video
              key={media.url}
              src={resolveMediaUrl(media.url, { preferProxy: true })}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${index === activeHeroIndex ? 'opacity-100' : 'opacity-0'}`}
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <MediaImage
              key={media.url}
              src={media.url}
              alt={`${selectedEvent.name} - ${index + 1}`}
              className={`absolute inset-0 h-full w-full object-cover transition-all duration-1000 ${index === activeHeroIndex ? 'scale-100 opacity-100' : 'scale-[1.03] opacity-0'}`}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          )
        )) : <div className="absolute inset-0 bg-[#26332f]" />}
        <div className="absolute inset-0 bg-black/38" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.7)_0%,rgba(0,0,0,0.28)_58%,rgba(0,0,0,0.52)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-transparent to-black/20" />

        <div className="relative mx-auto flex h-full max-w-[1440px] items-center px-5 pb-24 pt-16 lg:px-10">
          <div className="max-w-4xl">
            <div className="mb-5 flex flex-wrap items-center gap-3 text-xs font-medium uppercase tracking-[0.2em] text-white/75">
              <span>{eventTypeLabel(selectedEvent)}</span>
              {selectedEvent.event_tags?.slice(0, 2).map((tag) => (
                <span key={tag} className="border-l border-white/35 pl-3">{tag}</span>
              ))}
            </div>
            <h1 className="max-w-4xl font-serif text-5xl font-normal leading-[1.05] sm:text-6xl lg:text-8xl">
              {eventHostLabel(selectedEvent)}
            </h1>
            {selectedEvent.name !== eventHostLabel(selectedEvent) && (
              <p className="mt-4 text-lg text-white/78">{selectedEvent.name}</p>
            )}
            <div className="mt-7 flex flex-wrap gap-x-7 gap-y-3 text-sm text-white/88 md:text-base">
              {selectedEvent.city && <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{selectedEvent.city}</span>}
              {formatDateRange(selectedEvent) && <span className="flex items-center gap-2"><CalendarBlank className="h-4 w-4" />{formatDateRange(selectedEvent)}</span>}
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{stats.days || 1} day celebration</span>
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                asChild={Boolean(selectedEvent.highlight_video_url)}
                onClick={selectedEvent.highlight_video_url ? undefined : () => document.getElementById('moments')?.scrollIntoView({ behavior: 'smooth' })}
                className="h-12 rounded-none bg-white px-6 text-[#17211e] hover:bg-white/90"
              >
                {selectedEvent.highlight_video_url ? (
                  <a href={selectedEvent.highlight_video_url} target="_blank" rel="noreferrer"><Play className="mr-2 h-4 w-4" weight="fill" />Watch Highlights</a>
                ) : (
                  <span><Play className="mr-2 h-4 w-4" weight="fill" />View Highlights</span>
                )}
              </Button>
              <Button asChild variant="outline" className="h-12 rounded-none border-white/50 bg-black/10 px-6 text-white hover:bg-white/15 hover:text-white">
                <a href="#story"><ArrowDown className="mr-2 h-4 w-4" />Discover the Story</a>
              </Button>
            </div>
          </div>

          <div className="absolute bottom-8 right-5 hidden w-[310px] border border-white/20 bg-black/30 p-5 backdrop-blur-xl lg:block lg:right-10">
            <p className="text-[0.625rem] uppercase tracking-[0.2em] text-white/55">Planned and managed by</p>
            <div className="mt-4 flex items-center gap-4">
              {profile.profile_photo || profile.company_logo ? (
                <MediaImage src={profile.profile_photo || profile.company_logo} alt={profile.company_name || ''} className="h-14 w-14 rounded-full border border-white/30 object-cover" />
              ) : <GalagoLogo size={54} />}
              <div className="min-w-0">
                <p className="truncate font-serif text-xl">{profile.company_name}</p>
                <p className="mt-1 text-xs text-white/62">{profile.designation}</p>
              </div>
              {profile.verified && <Check className="ml-auto h-5 w-5 text-[var(--showcase-accent)]" weight="bold" />}
            </div>
          </div>

          {heroMedia.length > 1 && (
            <div className="absolute bottom-8 left-5 flex items-center gap-4 lg:left-10">
              <button
                type="button"
                onClick={() => setActiveHeroIndex((current) => (current === 0 ? heroMedia.length - 1 : current - 1))}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/35 bg-black/15 transition-colors hover:bg-white/15"
                aria-label="Previous hero media"
              >
                <CaretLeft className="h-5 w-5" />
              </button>
              <div className="flex gap-2">
                {heroMedia.map((media, index) => (
                  <button
                    key={`${media.url}-indicator`}
                    type="button"
                    onClick={() => setActiveHeroIndex(index)}
                    className={`h-1.5 transition-all ${index === activeHeroIndex ? 'w-9 bg-white' : 'w-4 bg-white/40 hover:bg-white/70'}`}
                    aria-label={`Show hero media ${index + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => setActiveHeroIndex((current) => (current + 1) % heroMedia.length)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/35 bg-black/15 transition-colors hover:bg-white/15"
                aria-label="Next hero media"
              >
                <CaretRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-[#e5e0d7] bg-white">
        <div className="mx-auto grid max-w-[1320px] grid-cols-2 px-5 sm:grid-cols-3 lg:grid-cols-6 lg:px-10">
          <SummaryMetric icon={Users} value={stats.guests ? `${stats.guests}+` : undefined} label="Guests" />
          <SummaryMetric icon={CalendarBlank} value={stats.days} label="Days" />
          <SummaryMetric icon={MapPin} value={destinationLabel(selectedEvent)} label="Destination" />
          <SummaryMetric icon={Heart} value={stats.functions} label="Functions" />
          {eventTypeConfig.features.showAccommodation && <SummaryMetric icon={Buildings} value={stats.hotels} label="Hotels" />}
          <SummaryMetric icon={SuitcaseRolling} value={stats.vendors} label="Vendors" />
        </div>
      </section>

      <section id="story" className="scroll-mt-24 bg-[#f5f3ee] py-24 lg:py-32">
        <div className="mx-auto grid max-w-[1320px] items-center gap-14 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:px-10">
          <div className="relative mx-auto w-full max-w-xl pb-12 pr-8">
            <MediaImage
              src={selectedEvent.gallery?.[1] || selectedEvent.cover_photo_url}
              alt={selectedEvent.name || 'Celebration story'}
              className="aspect-[4/5] w-full object-cover"
            />
            {selectedEvent.gallery?.[2] && (
              <MediaImage
                src={selectedEvent.gallery[2]}
                alt="A detail from the celebration"
                className="absolute bottom-0 right-0 aspect-square w-[42%] border-[10px] border-[#f5f3ee] object-cover"
              />
            )}
          </div>
          <div>
            <SectionHeading
              eyebrow={selectedEvent.story_subtitle || 'A celebration with soul'}
              title={selectedEvent.story_title || 'The Story'}
            />
            <p className="mt-7 whitespace-pre-line font-serif text-2xl leading-relaxed text-[#35413c]">
              {selectedEvent.story || selectedEvent.summary}
            </p>
            <div className="mt-10 flex flex-wrap gap-2">
              {(selectedEvent.event_tags || []).map((tag) => (
                <span key={tag} className="border border-[#cfc9bd] px-4 py-2 text-xs uppercase tracking-[0.14em] text-[#59645f]">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {moments.length > 0 && (
        <section id="moments" className="scroll-mt-24 bg-white py-24 lg:py-28">
          <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <SectionHeading eyebrow="The celebration, chapter by chapter" title="Top Moments" />
              <p className="max-w-md text-sm leading-7 text-[#69726e]">Explore each function and the thoughtful details that shaped the experience.</p>
            </div>
            <div className="hide-scrollbar mt-12 flex snap-x gap-5 overflow-x-auto pb-5">
              {moments.map((moment, index) => (
                <button
                  key={moment.id || moment.name}
                  type="button"
                  onClick={() => openMoment(moment)}
                  className="group relative aspect-[4/5] w-[78vw] max-w-[360px] shrink-0 snap-start overflow-hidden bg-[#dfe3df] text-left sm:w-[360px]"
                >
                  <MediaImage
                    src={moment.image_url || selectedEvent.gallery?.[index]}
                    alt={moment.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/78 via-black/5 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <p className="text-[0.625rem] uppercase tracking-[0.2em] text-white/60">Chapter {String(index + 1).padStart(2, '0')}</p>
                    <h3 className="mt-2 font-serif text-3xl">{moment.name}</h3>
                    <p className="mt-2 text-sm text-white/72">{[moment.venue, formatEventMonth(moment.date)].filter(Boolean).join(' / ')}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#eef0eb] py-24 lg:py-28">
        <div className="mx-auto grid max-w-[1320px] gap-14 px-5 lg:grid-cols-[0.75fr_1.25fr] lg:px-10">
          <SectionHeading
            eyebrow="Highlights of the celebration"
            title="Every detail, quietly taken care of."
            copy="From the first arrival to the final farewell, the planning team orchestrated every guest touchpoint and production detail."
          />
          <div className="grid sm:grid-cols-2">
            {(selectedEvent.services_delivered?.length ? selectedEvent.services_delivered : profile.services || []).map((service) => (
              <div key={service} className="flex items-center gap-4 border-b border-[#d3d8d1] py-5 sm:odd:pr-7 sm:even:border-l sm:even:pl-7">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[var(--showcase-primary)]">
                  <Check className="h-4 w-4" weight="bold" />
                </span>
                <span className="text-sm text-[#35413c] md:text-base">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="gallery" className="scroll-mt-24 bg-white py-24 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHeading eyebrow="Frames from the celebration" title="The Gallery" copy={`${galleryItems.length} published photographs`} />
            <div className="hide-scrollbar flex max-w-full gap-2 overflow-x-auto pb-1">
              {galleryFilters.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => {
                    setActiveGalleryFilter(filter);
                    recordInteraction('gallery_interaction');
                  }}
                  className={`h-10 shrink-0 border px-4 text-xs transition-colors ${activeGalleryFilter === filter ? 'border-[var(--showcase-primary)] bg-[var(--showcase-primary)] text-white' : 'border-[#d8d4cb] text-[#66706b] hover:border-[#929a96]'}`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {filteredGallery.length ? (
            <div className="mt-12 columns-2 gap-3 md:columns-3 lg:columns-4">
              {filteredGallery.map((item, index) => (
                <button
                  key={item.id || `${item.url}-${index}`}
                  type="button"
                  onClick={() => setLightboxIndex(index)}
                  className="group relative mb-3 block w-full break-inside-avoid overflow-hidden bg-[#e7eae6] text-left"
                >
                  <MediaImage
                    src={item.thumbnail_url || item.url}
                    alt={`${item.category} moment from ${selectedEvent.name}`}
                    className={`w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] ${index % 5 === 0 ? 'aspect-[3/4]' : index % 3 === 0 ? 'aspect-square' : 'aspect-[4/3]'}`}
                  />
                  <span className="absolute bottom-3 left-3 bg-black/55 px-2 py-1 text-[0.625rem] uppercase tracking-[0.15em] text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">{item.category}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-12 flex min-h-56 items-center justify-center border border-[#e2ded6] bg-[#f7f6f2] text-sm text-[#727b77]">
              No photographs have been tagged for {activeGalleryFilter} yet.
            </div>
          )}
        </div>
      </section>

      <section className="bg-[var(--showcase-primary)] py-24 text-white lg:py-28">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-10">
          <SectionHeading
            eyebrow="How we delivered this event"
            title="Beautiful on the surface. Precise behind the scenes."
            copy="A celebration of this scale is built on hundreds of details moving together. GalaGo's live event data tells the operational story."
            light
          />
          <div className="mt-14 grid grid-cols-2 border-l border-t border-white/18 md:grid-cols-4">
            {executionMetrics.map(([Icon, value, label]) => (
              <div key={label} className="min-h-44 border-b border-r border-white/18 p-6 md:p-8">
                <Icon className="h-7 w-7 text-[var(--showcase-accent)]" weight="light" />
                <p className="mt-8 font-serif text-4xl md:text-5xl">{value}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.15em] text-white/60">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {isEventFeatureEnabled(selectedEvent, 'showItinerary') && selectedEvent.itinerary?.length > 0 && (
        <section className="bg-[#f5f3ee] py-24 lg:py-28">
          <div className="mx-auto max-w-[1320px] px-5 lg:px-10">
            <SectionHeading eyebrow={showcaseConfig.journeyEyebrow} title={showcaseConfig.journeyTitle} align="center" />
            <div className="relative mt-14 grid gap-8 md:grid-cols-4 lg:grid-cols-7">
              <div className="absolute left-[7%] right-[7%] top-5 hidden border-t border-dashed border-[#b9b4aa] md:block" />
              {['Planning', 'Venue Selection', 'Guest Invitations', 'Hospitality', 'Celebration Moments', 'Main Event', 'Farewell'].map((step, index) => (
                <div key={step} className="relative text-center">
                  <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-[#bcb6aa] bg-[#f5f3ee] font-serif text-sm text-[var(--showcase-primary)]">{index + 1}</span>
                  <p className="mt-4 text-sm text-[#4d5853]">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {currentTestimonial && (
        <section className="bg-white py-24 lg:py-32">
          <div className="mx-auto grid max-w-[1120px] items-center gap-12 px-5 lg:grid-cols-[280px_1fr] lg:px-10">
            <div className="mx-auto h-64 w-64 overflow-hidden rounded-full bg-[#e7eae6] lg:mx-0">
              {currentTestimonial.photo ? (
                <MediaImage src={currentTestimonial.photo} alt={showcaseConfig.testimonialPhotoAlt} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[var(--showcase-primary)]"><Heart className="h-16 w-16" weight="light" /></div>
              )}
            </div>
            <div>
              <Quotes className="h-12 w-12 text-[var(--showcase-accent)]" weight="fill" />
              <RatingStars rating={currentTestimonial.rating} className="mt-7" />
              <blockquote className="mt-6 font-serif text-3xl leading-relaxed text-[#27322e] md:text-4xl">
                "{currentTestimonial.review || currentTestimonial.message}"
              </blockquote>
              <p className="mt-7 text-sm font-semibold uppercase tracking-[0.14em] text-[#43504a]">
                {[currentTestimonial.bride_name, currentTestimonial.groom_name].filter(Boolean).join(' & ') || currentTestimonial.guest_name}
              </p>
              <p className="mt-2 text-sm text-[#7a837f]">
                {[currentTestimonial.location, formatEventMonth(currentTestimonial.wedding_date)].filter(Boolean).join(' / ')}
              </p>
              {testimonials.length > 1 && (
                <div className="mt-9 flex gap-2">
                  <button type="button" onClick={() => setTestimonialIndex((current) => (current === 0 ? testimonials.length - 1 : current - 1))} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#d6d1c8] hover:bg-[#f3f1ec]" aria-label="Previous testimonial"><CaretLeft className="h-5 w-5" /></button>
                  <button type="button" onClick={() => setTestimonialIndex((current) => (current + 1) % testimonials.length)} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#d6d1c8] hover:bg-[#f3f1ec]" aria-label="Next testimonial"><CaretRight className="h-5 w-5" /></button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section id="about" className="scroll-mt-24 bg-[#e9ece7] py-24 lg:py-28">
        <div className="mx-auto grid max-w-[1320px] items-center gap-14 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:px-10">
          <div className="relative mx-auto aspect-[4/5] w-full max-w-lg overflow-hidden bg-[#dce1dc]">
            <MediaImage src={profile.profile_photo || profile.company_logo} alt={profile.company_name || 'Event planner'} className="h-full w-full object-cover" />
            {profile.verified && (
              <span className="absolute bottom-5 left-5 flex items-center gap-2 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--showcase-primary)]">
                <Check className="h-4 w-4" weight="bold" /> Verified Planner
              </span>
            )}
          </div>
          <div>
            <SectionHeading eyebrow="Managed by" title={profile.company_name || 'Your planning team'} copy={profile.designation} />
            <p className="mt-7 text-base leading-8 text-[#53605a]">{profile.about}</p>
            <div className="mt-9 grid grid-cols-2 gap-x-8 gap-y-6 border-y border-[#cdd2cc] py-7 sm:grid-cols-4">
              <div><p className="font-serif text-3xl">{profile.years_experience || 0}+</p><p className="mt-1 text-xs text-[#707a75]">Years</p></div>
              <div><p className="font-serif text-3xl">{profile.weddings_count || 0}+</p><p className="mt-1 text-xs text-[#707a75]">Celebrations</p></div>
              <div><p className="font-serif text-3xl">{profile.corporate_events_count || 0}+</p><p className="mt-1 text-xs text-[#707a75]">Corporate</p></div>
              <div><p className="font-serif text-3xl">{data.ratings?.average || 'New'}</p><p className="mt-1 text-xs text-[#707a75]">Average rating</p></div>
            </div>
            <div className="mt-7 flex flex-wrap gap-2">
              {(profile.specializations || []).map((item) => (
                <span key={item} className="border border-[#c8cec7] bg-white/60 px-4 py-2 text-xs text-[#4e5a55]">{item}</span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {SOCIAL_LINKS.map(([key, Icon, label]) => {
                const href = profile.social_links?.[key] || (key === 'website' ? profile.company_website : '');
                if (!href) return null;
                return (
                  <a key={key} href={href} target="_blank" rel="noreferrer" aria-label={label} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#bdc5bd] text-[#46524c] transition-colors hover:border-[var(--showcase-primary)] hover:text-[var(--showcase-primary)]">
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
              {profile.availability && <span className="ml-0 flex items-center gap-2 text-sm text-[#52605a] sm:ml-3"><span className="h-2 w-2 rounded-full bg-emerald-600" />{profile.availability}</span>}
            </div>
          </div>
        </div>
      </section>

      {profile.awards?.length > 0 && (
        <section className="border-b border-[#e1ddd4] bg-white py-20">
          <div className="mx-auto max-w-[1320px] px-5 lg:px-10">
            <SectionHeading eyebrow="Press and recognition" title={showcaseConfig.recognitionTitle} />
            <div className="mt-12 grid border-l border-t border-[#e1ddd4] sm:grid-cols-2 lg:grid-cols-4">
              {profile.awards.slice(0, 8).map((award) => (
                <div key={award.id || award.name} className="min-h-56 border-b border-r border-[#e1ddd4] p-7">
                  {award.award_logo ? <MediaImage src={award.award_logo} alt="" className="h-10 w-auto object-contain" /> : <Star className="h-8 w-8 text-[var(--showcase-accent)]" weight="light" />}
                  <p className="mt-8 font-serif text-xl">{award.name}</p>
                  <p className="mt-2 text-sm text-[#737c78]">{[award.organization, award.year].filter(Boolean).join(' / ')}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {Object.keys(vendorGroups).length > 0 && (
        <section className="bg-[#f5f3ee] py-24 lg:py-28">
          <div className="mx-auto max-w-[1320px] px-5 lg:px-10">
            <SectionHeading eyebrow="The creative collective" title="Trusted Partners" copy="A celebration brought to life by specialists chosen for their craft, consistency, and care." align="center" />
            <div className="mt-14 grid gap-x-12 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
              {Object.entries(vendorGroups).map(([category, vendors]) => (
                <div key={category} className="border-t border-[#cbc6bc] pt-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.17em] text-[#9b7c48]">{VENDOR_LABELS[category] || category}</p>
                  <div className="mt-5 space-y-4">
                    {vendors.map((vendor) => {
                      const href = vendor.website || vendor.instagram;
                      const content = <span className="font-serif text-xl text-[#2d3934]">{vendor.name}</span>;
                      return href ? <a key={vendor.id || vendor.name} href={href} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-3 hover:text-[var(--showcase-primary)]">{content}<ArrowUpRight className="h-4 w-4" /></a> : <div key={vendor.id || vendor.name}>{content}</div>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="events" className="scroll-mt-24 bg-white py-24 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionHeading eyebrow="More from the portfolio" title="Featured Events" />
            <p className="max-w-sm text-sm leading-7 text-[#69726e]">Distinct places, personal stories, and one consistent standard of delivery.</p>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {(data.events || []).map((event) => <EventCard key={event.id} event={event} slug={slug} active={event.id === selectedEvent.id} />)}
          </div>
        </div>
      </section>

      {(profile.instagram_posts?.length > 0 || profile.instagram_url) && (
        <section className="border-t border-[#e5e1d9] bg-[#f5f3ee] py-24">
          <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
            <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
              <SectionHeading eyebrow="Behind the scenes" title="Follow the Journey" />
              {profile.instagram_url && (
                <a href={profile.instagram_url} target="_blank" rel="noreferrer" onClick={() => recordInteraction('instagram_click')} className="inline-flex items-center gap-2 text-sm font-medium text-[var(--showcase-primary)]">
                  <InstagramLogo className="h-5 w-5" /> View on Instagram <ArrowUpRight className="h-4 w-4" />
                </a>
              )}
            </div>
            <div className="mt-10 grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
              {(profile.instagram_posts?.length ? profile.instagram_posts : selectedEvent.gallery || []).slice(0, 6).map((photo, index) => (
                <a key={`${photo}-${index}`} href={profile.instagram_url || '#gallery'} target={profile.instagram_url ? '_blank' : undefined} rel={profile.instagram_url ? 'noreferrer' : undefined} className="group relative aspect-square overflow-hidden bg-[#e3e6e2]">
                  <MediaImage src={photo} alt="Instagram moment" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-white opacity-0 transition-all group-hover:bg-black/25 group-hover:opacity-100"><InstagramLogo className="h-6 w-6" /></span>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="contact" className="scroll-mt-24 bg-[#1e2925] py-24 text-white lg:py-32">
        <div className="mx-auto max-w-5xl px-5 text-center lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--showcase-accent)]">Begin your celebration</p>
          <h2 className="mx-auto mt-5 max-w-4xl font-serif text-5xl font-normal leading-tight md:text-7xl">Let's create something people will remember.</h2>
          <p className="mx-auto mt-7 max-w-2xl text-base leading-8 text-white/62">Share your date, destination, and vision. The planning team will take it from there.</p>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            {whatsappUrl && profile.contact?.whatsapp_enabled !== false && (
              <Button asChild className="h-12 rounded-none bg-white px-7 text-[#17211e] hover:bg-white/90">
                <a href={whatsappUrl} target="_blank" rel="noreferrer" onClick={() => recordInteraction('whatsapp_click')}><WhatsappLogo className="mr-2 h-5 w-5" />Chat on WhatsApp</a>
              </Button>
            )}
            <Button onClick={() => openInquiry('Book a consultation')} variant="outline" className="h-12 rounded-none border-white/35 bg-transparent px-7 text-white hover:bg-white/10 hover:text-white">
              <CalendarBlank className="mr-2 h-5 w-5" />Book Consultation
            </Button>
          </div>
          {(profile.cities_served?.length > 0 || profile.next_available_month) && (
            <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-white/54">
              {profile.next_available_month && <span>Next availability: {profile.next_available_month}</span>}
              {profile.cities_served?.length > 0 && <span>Serving {profile.cities_served.join(', ')}</span>}
            </div>
          )}
        </div>
      </section>

      <footer className="bg-[#131b18] pb-28 pt-12 text-white/62 md:pb-10">
        <div className="mx-auto flex max-w-[1320px] flex-col justify-between gap-8 px-5 md:flex-row md:items-center lg:px-10">
          <div className="flex items-center gap-3">
            {profile.company_logo ? <MediaImage src={profile.company_logo} alt="" className="h-9 w-9 object-contain" /> : <GalagoLogo size={36} />}
            <div><p className="font-serif text-lg text-white">{profile.company_name}</p><p className="mt-1 text-xs">Portfolio powered by GalaGo</p></div>
          </div>
          <div className="flex flex-wrap items-center gap-5 text-xs">
            {profile.contact?.privacy_url && <a href={profile.contact.privacy_url} target="_blank" rel="noreferrer" className="hover:text-white">Privacy</a>}
            {profile.contact?.terms_url && <a href={profile.contact.terms_url} target="_blank" rel="noreferrer" className="hover:text-white">Terms</a>}
            {profile.company_website && <a href={profile.company_website} target="_blank" rel="noreferrer" className="hover:text-white">Website</a>}
            <span>(c) {new Date().getFullYear()} {profile.company_name}. All rights reserved.</span>
          </div>
        </div>
      </footer>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-black/10 bg-white/96 p-3 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] backdrop-blur-xl md:hidden">
        <div className="mx-auto grid max-w-lg grid-cols-2 gap-2">
          {whatsappUrl && profile.contact?.whatsapp_enabled !== false && (
            <Button asChild variant="outline" className="h-11 rounded-none border-[#ced3cd] md:w-auto">
              <a href={whatsappUrl} target="_blank" rel="noreferrer" onClick={() => recordInteraction('whatsapp_click')}><WhatsappLogo className="mr-2 h-5 w-5 text-emerald-700" />WhatsApp</a>
            </Button>
          )}
          <Button onClick={() => openInquiry('Request a quote')} className="h-11 rounded-none text-white hover:opacity-90" style={{ backgroundColor: primaryColor }}>
            <EnvelopeSimple className="mr-2 h-5 w-5" />Request Quote
          </Button>
        </div>
      </div>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-2xl rounded-none border-0 p-0">
          <div className="bg-[var(--showcase-primary)] px-7 py-8 text-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl font-normal">Tell us about your event</DialogTitle>
              <DialogDescription className="mt-2 text-white/65">A few details are enough to begin the conversation.</DialogDescription>
            </DialogHeader>
          </div>
          <form className="grid gap-5 p-7 sm:grid-cols-2" onSubmit={submitInquiry}>
            <div className="space-y-2 sm:col-span-2"><Label htmlFor="lead-name">Name</Label><Input id="lead-name" className="h-11 rounded-none" value={contact.name} onChange={(event) => setContact((current) => ({ ...current, name: event.target.value }))} required /></div>
            <div className="space-y-2"><Label htmlFor="lead-phone">Phone</Label><Input id="lead-phone" className="h-11 rounded-none" value={contact.phone} onChange={(event) => setContact((current) => ({ ...current, phone: event.target.value }))} /></div>
            <div className="space-y-2"><Label htmlFor="lead-email">Email</Label><Input id="lead-email" type="email" className="h-11 rounded-none" value={contact.email} onChange={(event) => setContact((current) => ({ ...current, email: event.target.value }))} /></div>
            <EventAwareDatePicker id="lead-date" label="Event Date" value={contact.event_date} onChange={(date) => setContact((current) => ({ ...current, event_date: date }))} defaultToEventStart={false} triggerClassName="h-11 rounded-none" />
            <div className="space-y-2"><Label htmlFor="lead-budget">Budget</Label><Input id="lead-budget" className="h-11 rounded-none" placeholder="e.g. Rs. 25-40 lakh" value={contact.budget} onChange={(event) => setContact((current) => ({ ...current, budget: event.target.value }))} /></div>
            <div className="space-y-2 sm:col-span-2"><Label htmlFor="lead-location">Preferred Location</Label><Input id="lead-location" className="h-11 rounded-none" value={contact.location} onChange={(event) => setContact((current) => ({ ...current, location: event.target.value }))} /></div>
            <div className="space-y-2 sm:col-span-2"><Label htmlFor="lead-requirements">What are you envisioning?</Label><Textarea id="lead-requirements" className="rounded-none" rows={4} value={contact.requirements} onChange={(event) => setContact((current) => ({ ...current, requirements: event.target.value }))} /></div>
            <p className="text-xs leading-5 text-[#7a837f] sm:col-span-2">Please provide either a phone number or email so the planning team can respond.</p>
            <Button type="submit" className="h-12 rounded-none text-white hover:opacity-90 sm:col-span-2" style={{ backgroundColor: primaryColor }} disabled={sending}>{sending ? 'Sending...' : 'Request a Consultation'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={lightboxIndex !== null} onOpenChange={(open) => !open && setLightboxIndex(null)}>
        <DialogContent className="h-[94vh] max-h-none w-[96vw] max-w-[1500px] overflow-hidden rounded-none border-0 bg-black p-0 text-white">
          {lightboxIndex !== null && filteredGallery[lightboxIndex] && (
            <div className="relative flex h-full items-center justify-center p-5 md:p-12">
              <MediaImage src={filteredGallery[lightboxIndex].url} alt={filteredGallery[lightboxIndex].category || 'Gallery image'} className="max-h-full max-w-full object-contain" loading="eager" />
              <button type="button" onClick={() => setLightboxIndex(null)} className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-black/55" aria-label="Close gallery"><X className="h-5 w-5" /></button>
              {filteredGallery.length > 1 && (
                <>
                  <button type="button" onClick={() => setLightboxIndex((current) => (current === 0 ? filteredGallery.length - 1 : current - 1))} className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full bg-black/55" aria-label="Previous image"><CaretLeft className="h-6 w-6" /></button>
                  <button type="button" onClick={() => setLightboxIndex((current) => (current + 1) % filteredGallery.length)} className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-full bg-black/55" aria-label="Next image"><CaretRight className="h-6 w-6" /></button>
                </>
              )}
              <p className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-black/55 px-4 py-2 text-xs uppercase tracking-[0.16em]">{filteredGallery[lightboxIndex].category} / {lightboxIndex + 1} of {filteredGallery.length}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
